/**
 * Package overview — reads every euro-package-room selection on the page
 * (via window.EuroPackageState) and renders the "Selected Overview" rows.
 *
 * Cart rule (V1):
 *  - The whole package = ONE real product (variant from section settings).
 *  - Each fully-selected room becomes ONE line item property:
 *      key:   roomTitle   (truncated to 100 chars)
 *      value: planTitle   (truncated to 255 chars)
 *  - "Add to cart" is disabled until EVERY rendered room has a selection.
 */
class EuroPackageOverview extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.sectionId = this.dataset.sectionId || '';
    this.variantId = this.dataset.productVariant ? Number(this.dataset.productVariant) : null;

    this.listEl = this.querySelector('[data-overview-list]');
    this.emptyEl = this.querySelector('[data-overview-empty]');
    this.addBtn = this.querySelector('[data-overview-add]');
    this.submitBtn = this.querySelector('[data-overview-submit]');
    this.hintEl = this.querySelector('[data-overview-hint]');

    // Contact popup
    this.modalEl = this.querySelector('[data-overview-modal]');
    this.packageField = this.querySelector('[data-overview-package-field]');
    this.modalOverlay = this.querySelector('[data-overview-modal] .euro-package-overview__modal-overlay');

    this.noteUnselected =
      this.dataset.noteUnselected ||
      'Please select a look for every room before adding the package to cart.';
    this.noteNoProduct =
      this.dataset.noteNoProduct || 'No package product configured yet. Pick one in this section\u2019s settings.';
    this.addLabel = this.addBtn ? this.addBtn.textContent.trim() : 'Add Package to Cart';

    this.onAddClick = () => this.addToCart();
    this.onSubmitClick = () => this.openModal();
    this.onCloseClick = () => this.closeModal();
    this.onModalKeydown = (event) => {
      if (event.key === 'Escape') this.closeModal();
    };
    this.onModalClick = (event) => {
      if (event.target === this.modalOverlay) this.closeModal();
    };
    this.onFormSubmit = () => this.syncPackageField();

    this.addBtn && this.addBtn.addEventListener('click', this.onAddClick);
    this.submitBtn && this.submitBtn.addEventListener('click', this.onSubmitClick);

    if (this.modalEl) {
      this.modalEl.addEventListener('click', this.onModalClick);
      this.modalEl.addEventListener('keydown', this.onModalKeydown);
      this.modalEl
        .querySelectorAll('[data-overview-modal-close]')
        .forEach((el) => el.addEventListener('click', this.onCloseClick));
      const form = this.modalEl.querySelector('form');
      if (form) form.addEventListener('submit', this.onFormSubmit);
    }

    // Re-render whenever any room writes/clears its selection.
    this.unsubscribe = window.EuroPackageState
      ? window.EuroPackageState.subscribe(() => this.render())
      : function () {};

    this.render();

    // Late async preselections (rooms initialise their Swiper lazily).
    setTimeout(() => this.render(), 250);
    setTimeout(() => this.render(), 1200);

    // Native `{% form 'contact' %}` success reloads with ?contact_posted=true.
    // Re-open the popup so the customer sees the "Thank you" confirmation
    // (the success state is rendered inside the popup).
    if (this.isContactPosted()) {
      setTimeout(() => this.openModal(), 300);
    }
  }

  isContactPosted() {
    try {
      const params = new URLSearchParams(this.ownerDocument.location.search);
      return params.get('contact_posted') === 'true';
    } catch (err) {
      return false;
    }
  }

  disconnectedCallback() {
    if (typeof this.unsubscribe === 'function') this.unsubscribe();
    this.addBtn && this.addBtn.removeEventListener('click', this.onAddClick);
    this.submitBtn && this.submitBtn.removeEventListener('click', this.onSubmitClick);
    if (this.modalEl) {
      this.modalEl.removeEventListener('click', this.onModalClick);
      this.modalEl.removeEventListener('keydown', this.onModalKeydown);
      this.modalEl
        .querySelectorAll('[data-overview-modal-close]')
        .forEach((el) => el.removeEventListener('click', this.onCloseClick));
      const form = this.modalEl.querySelector('form');
      if (form) form.removeEventListener('submit', this.onFormSubmit);
    }
  }

  /* --------------------------------------------------- contact popup */

  openModal() {
    if (!this.modalEl) return;
    this.syncPackageField();
    this.lastTrigger = this.ownerDocument.activeElement;
    this.modalEl.hidden = false;
    this.modalEl.setAttribute('aria-hidden', 'false');
    this.ownerDocument.body.classList.add('euro-package-overview__scroll-lock');
    const dialog = this.modalEl.querySelector('.euro-package-overview__modal-dialog');
    if (dialog && typeof dialog.focus === 'function') dialog.focus();
  }

  closeModal() {
    if (!this.modalEl) return;
    this.modalEl.hidden = true;
    this.modalEl.setAttribute('aria-hidden', 'true');
    this.ownerDocument.body.classList.remove('euro-package-overview__scroll-lock');
    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
      this.lastTrigger.focus();
    }
  }

  /**
   * Serialize every room's selection into the hidden contact field.
   * Format per selected room:
   *   Room Title: Plan Title — 2 Item A, 1 Item B
   */
  syncPackageField() {
    if (!this.packageField) return;
    const lines = this.roomEntries()
      .filter((entry) => entry.state)
      .map((entry) => {
        const room = entry.roomTitle || '';
        const plan = entry.state.planTitle || '';
        const detail = this.packageDetailText(entry.state);
        const head = room + ': ' + plan;
        return detail ? head + ' — ' + detail : head;
      });
    this.packageField.value = lines.join('\n').slice(0, 4000);
  }

  /** Human-readable contents of one plan: "2 Sofa, 1 Coffee Table". */
  packageDetailText(state) {
    const items = Array.isArray(state.items) ? state.items : [];
    if (items.length) {
      return items
        .map((item) => {
          const qty = item && item.quantity != null ? item.quantity : 1;
          const title = item && item.title ? item.title : '';
          return title ? (qty > 0 ? qty + ' ' + title : title) : '';
        })
        .filter(Boolean)
        .join(', ');
    }
    // Fall back to the plan's | -separated summary when items aren't available.
    return String(state.summary || '')
      .split('|')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ');
  }

  /* ---------------------------------------------------------- room state */

  rooms() {
    return Array.prototype.slice.call(this.ownerDocument.querySelectorAll('euro-package-room'));
  }

  /** @returns [{ sectionId, roomTitle, state|null }] in DOM order */
  roomEntries() {
    return this.rooms().map((roomEl) => {
      const sectionId = roomEl.dataset.sectionId || '';
      const roomTitle = roomEl.dataset.roomTitle || '';
      const state = sectionId && window.EuroPackageState ? window.EuroPackageState.get(sectionId) : null;
      return {
        sectionId,
        roomTitle: (state && state.roomTitle) || roomTitle,
        state: state && state.planId ? state : null,
      };
    });
  }

  /* -------------------------------------------------------------- render */

  render() {
    const entries = this.roomEntries();
    const selected = entries.filter((entry) => entry.state);
    const hasRooms = entries.length > 0;
    const allSelected = hasRooms && selected.length === entries.length;

    this.renderCards(selected);

    // Eligibility for add-to-cart
    const canAdd = allSelected && !!this.variantId;
    if (this.addBtn) {
      if (canAdd) {
        this.addBtn.disabled = false;
        this.addBtn.classList.remove('is-disabled');
      } else {
        this.addBtn.disabled = true;
        this.addBtn.classList.add('is-disabled');
      }
      this.addBtn.textContent = this.addLabel;
    }

    this.renderHint(hasRooms, allSelected);
  }

  renderCards(selected) {
    if (!this.listEl) return;

    if (!selected.length) {
      this.listEl.innerHTML = '';
      this.listEl.hidden = true;
      if (this.emptyEl) this.emptyEl.hidden = false;
      return;
    }

    if (this.emptyEl) this.emptyEl.hidden = true;
    this.listEl.hidden = false;

    this.listEl.innerHTML = selected
      .map((entry, index) => this.cardHtml(entry, index === 0))
      .join('');
  }

  cardHtml(entry, isFirst) {
    const esc = this.escapeHtml;
    const state = entry.state;
    const roomTitle = esc(state.roomTitle || entry.roomTitle || '');
    const planTitle = esc(state.planTitle || '');
    const image = state.planOverviewImage
      ? String(state.planOverviewImage)
      : state.planImage
        ? String(state.planImage)
        : '';
    const alt = esc(state.planTitle || 'Selected plan');
    const itemsHtml = this.itemsHtml(state);

    const mediaHtml = image
      ? '<div class="euro-package-overview__media" aria-hidden="true"><img src="' +
        esc(image) +
        '" alt="' +
        alt +
        '" loading="lazy"></div>'
      : '<div class="euro-package-overview__media euro-package-overview__media--placeholder" aria-hidden="true"></div>';

    const mobile =
      '<div class="euro-package-overview__layout euro-package-overview__layout--mobile">' +
      '<div class="euro-package-overview__room">' +
      '<span class="euro-package-overview__room-name">' +
      roomTitle +
      '</span>' +
      '<strong class="euro-package-overview__room-plan">' +
      planTitle +
      '</strong>' +
      '</div>' +
      mediaHtml +
      itemsHtml +
      '</div>';

    const desktop =
      '<div class="euro-package-overview__layout euro-package-overview__layout--desktop">' +
      '<span class="euro-package-overview__room-name">' +
      roomTitle +
      '</span>' +
      '<strong class="euro-package-overview__room-plan">' +
      planTitle +
      '</strong>' +
      itemsHtml +
      mediaHtml +
      '</div>';

    return (
      '<div class="euro-package-overview__card' +
      (isFirst ? ' euro-package-overview__card--first' : '') +
      '" data-overview-card>' +
      mobile +
      desktop +
      '</div>'
    );
  }

  itemsHtml(state) {
    const esc = this.escapeHtml;
    const items = Array.isArray(state.items) ? state.items : [];
    const lines = items.length
      ? items.map((item) => {
          const qty = item && item.quantity != null ? item.quantity : 1;
          const title = item && item.title ? item.title : '';
          return (qty > 0 ? qty + ' ' : '') + esc(title);
        })
      : String(state.summary || '')
          .split('|')
          .map((part) => part.trim())
          .filter(Boolean)
          .map(esc);

    if (!lines.length) return '';
    return (
      '<ul class="euro-package-overview__items">' +
      lines.map((line) => '<li>' + line + '</li>').join('') +
      '</ul>'
    );
  }

  renderHint(hasRooms, allSelected) {
    if (!this.hintEl) return;

    let message = '';
    let isError = false;

    if (!hasRooms || !allSelected) {
      message = this.noteUnselected || '';
    } else if (!this.variantId) {
      message = this.noteNoProduct || '';
    }

    this.hintEl.textContent = message;
    this.hintEl.classList.toggle('is-error', isError);
    this.hintEl.hidden = !message;
  }

  /* ------------------------------------------------------------ add cart */

  async addToCart() {
    if (!this.variantId || this.addBtn.disabled) return;

    const selected = this.roomEntries().filter((entry) => entry.state);
    if (!selected.length || selected.length !== this.roomEntries().length) return;

    const properties = {};
    selected.forEach((entry) => {
      const key = this.truncate(entry.roomTitle, 100);
      const plan = entry.state.planTitle || '';
      const detail = this.packageDetailText(entry.state);
      // value: "Plan Title" or "Plan Title — 2 Sofa, 1 Coffee Table"
      const value = this.truncate(detail ? plan + ' — ' + detail : plan, 255);
      if (key) properties[key] = value;
    });

    const items = [
      {
        id: this.variantId,
        quantity: 1,
        properties: properties,
      },
    ];

    const cartAddUrl =
      (typeof window !== 'undefined' && window.routes && window.routes.cart_add_url) ||
      '/cart/add.js';

    this.setAdding(true);

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (err) {
        payload = null;
      }

      if (!response.ok) {
        const message = payload && payload.message ? payload.message : 'Could not add to cart.';
        throw new Error(message);
      }

      this.addBtn.textContent = this.addLabel;
      this.setSuccess();

      // Always take the customer to the full cart page after a successful add.
      // This keeps the package flow consistent across notification, drawer,
      // and page cart configurations.
      const cartUrl =
        (typeof window !== 'undefined' && window.routes && window.routes.cart_url) || '/cart';
      window.location.href = cartUrl;
    } catch (err) {
      this.setAdding(false);
      if (this.hintEl) {
        this.hintEl.textContent = err && err.message ? err.message : 'Could not add to cart.';
        this.hintEl.classList.add('is-error');
        this.hintEl.hidden = false;
      }
    }
  }

  setAdding(isAdding) {
    if (!this.addBtn) return;
    this.addBtn.classList.toggle('is-loading', isAdding);
    if (isAdding) {
      this.addBtn.textContent = 'Adding…';
    } else {
      this.addBtn.textContent = this.addLabel;
    }
  }

  setSuccess() {
    if (!this.addBtn) return;
    this.addBtn.textContent = 'Added to Cart';
    this.addBtn.classList.add('is-success');
    setTimeout(() => {
      this.addBtn.classList.remove('is-success');
      this.addBtn.textContent = this.addLabel;
    }, 2500);
  }

  /* ------------------------------------------------------------ helpers */

  escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  truncate(value, max) {
    let text = String(value == null ? '' : value).trim();
    if (text.length > max) {
      text = text.slice(0, max);
    }
    return text;
  }
}

if (!customElements.get('euro-package-overview')) {
  customElements.define('euro-package-overview', EuroPackageOverview);
}
